import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Paperclip, Send, FileText, Plus } from "lucide-react";
import { toast } from "sonner";
import { authApi, usd, STATUS_LABELS, STATUS_COLORS, FACILITY_LABELS, BUDGET_LABELS, TIMELINE_LABELS, QUOTE_STATUS_COLORS, attachmentUrl } from "@/lib/auth";
import { AdminHeader } from "@/pages/admin/Dashboard";

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [lead, setLead] = useState(null);
  const [note, setNote] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    authApi.me()
      .then(setUser)
      .catch(() => navigate("/admin", { replace: true }));
  }, [navigate]);

  useEffect(() => {
    if (!user) return;
    authApi.lead(leadId).then(setLead).catch(() => navigate("/admin/dashboard"));
    authApi.quotesForLead(leadId).then((d) => setQuotes(d.quotes)).catch(() => {});
  }, [user, leadId, navigate]);

  const changeStatus = async (status) => {
    setSaving(true);
    try {
      await authApi.setStatus(leadId, status);
      setLead((l) => ({ ...l, status }));
      toast.success(`Status updated to ${STATUS_LABELS[status]}`);
    } catch {
      toast.error("Could not update status");
    } finally {
      setSaving(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const n = await authApi.addNote(leadId, note.trim());
      setLead((l) => ({ ...l, notes: [...(l.notes || []), n] }));
      setNote("");
    } catch {
      toast.error("Could not save note");
    } finally {
      setSaving(false);
    }
  };

  if (!user || !lead) {
    return <main className="min-h-screen bg-void flex items-center justify-center"><p className="font-mono-tech text-xs uppercase tracking-[0.3em] text-white/40">Loading…</p></main>;
  }

  const fields = [
    ["Email", lead.email], ["Phone", lead.phone], ["Location", lead.location],
    ["Facility", FACILITY_LABELS[lead.facility_type] || lead.facility_type],
    ["Holes / Area", lead.holes_or_area],
    ["Existing Lighting", lead.existing_lighting],
    ["Budget", BUDGET_LABELS[lead.budget] || lead.budget],
    ["Timeline", TIMELINE_LABELS[lead.timeline] || lead.timeline],
  ];

  return (
    <main className="min-h-screen bg-void" data-testid="admin-lead-detail">
      <AdminHeader user={user} />
      <div className="px-4 sm:px-8 max-w-5xl mx-auto py-10">
        <button onClick={() => navigate("/admin/dashboard")} data-testid="lead-back-btn" className="inline-flex items-center gap-2 text-xs text-white/40 hover:text-white transition-colors mb-8">
          <ArrowLeft size={14} /> All leads
        </button>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-6 mb-10">
          <div>
            <p className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2" data-testid="lead-id-label">{lead.lead_id}</p>
            <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight" data-testid="lead-company">{lead.company}</h1>
            <p className="text-muted-green text-sm mt-1" data-testid="lead-contact-name">{lead.full_name} · {lead.location}</p>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-sm border text-xs ${STATUS_COLORS[lead.status]}`} data-testid="lead-current-status">
              {STATUS_LABELS[lead.status]}
            </span>
            <select
              data-testid="lead-status-select"
              value={lead.status}
              disabled={saving}
              onChange={(e) => changeStatus(e.target.value)}
              className="bg-[#161B19] border hairline rounded-sm text-xs text-white px-3 py-2 focus:outline-none focus:border-[#A7D7A0]/60"
            >
              {Object.entries(STATUS_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-card-dark border hairline rounded-md p-7">
            <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-6">Project Details</h2>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
              {fields.map(([k, v]) => (
                <div key={k}>
                  <dt className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/35">{k}</dt>
                  <dd className="mt-1 text-sm text-white/85 break-words">{v || "—"}</dd>
                </div>
              ))}
            </dl>
            {lead.description && (
              <div className="mt-6 pt-6 border-t hairline">
                <p className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/35 mb-2">Description</p>
                <p className="text-sm text-white/75 leading-relaxed" data-testid="lead-description">{lead.description}</p>
              </div>
            )}
            {lead.calculator && (
              <div className="mt-6 pt-6 border-t hairline">
                <p className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/35 mb-2">Calculator Context</p>
                <p className="text-sm text-white/75">
                  {lead.calculator.holes} holes · {lead.calculator.rounds_per_night} rounds/night · ${lead.calculator.revenue_per_round}/round · {lead.calculator.nights_per_month} nights/mo
                </p>
              </div>
            )}
            {lead.attachments?.length > 0 && (
              <div className="mt-6 pt-6 border-t hairline">
                <p className="text-[10px] font-mono-tech uppercase tracking-[0.2em] text-white/35 mb-3">Attachments</p>
                <ul className="space-y-2">
                  {lead.attachments.map((a) => (
                    <li key={a.filename}>
                      <a href={attachmentUrl(a.filename)} target="_blank" rel="noreferrer" data-testid={`lead-attachment-${a.filename}`} className="inline-flex items-center gap-2 text-sm text-[#A7D7A0] hover:text-white transition-colors">
                        <Paperclip size={13} /> {a.original}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-card-dark border hairline rounded-md p-7 flex flex-col">
            <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0] mb-6">Internal Notes</h2>
            <div className="flex-1 space-y-4 mb-6 max-h-80 overflow-y-auto" data-testid="lead-notes-list">
              {(lead.notes || []).length === 0 && <p className="text-white/30 text-sm">No notes yet.</p>}
              {(lead.notes || []).map((n) => (
                <div key={n.note_id} className="bg-white/[0.03] rounded-sm p-4">
                  <p className="text-sm text-white/85 leading-relaxed">{n.text}</p>
                  <p className="text-[10px] text-white/30 mt-2 font-mono-tech">
                    {n.author} · {new Date(n.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                data-testid="lead-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addNote()}
                placeholder="Add an internal note…"
                className="flex-1 bg-white/[0.04] border hairline rounded-sm px-4 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#A7D7A0]/60"
              />
              <button
                data-testid="lead-note-add-btn"
                onClick={addNote}
                disabled={saving || !note.trim()}
                className="inline-flex items-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-white transition-colors disabled:opacity-40"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-6 bg-card-dark border hairline rounded-md p-7" data-testid="lead-quotes-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-[#A7D7A0]">Quotes</h2>
            <button
              onClick={() => navigate(`/admin/leads/${leadId}/quotes/new`)}
              data-testid="new-quote-btn"
              className="inline-flex items-center gap-2 bg-[#A7D7A0] text-[#101214] font-semibold text-xs px-4 py-2.5 rounded-sm hover:bg-white transition-colors"
            >
              <Plus size={13} /> New Quote
            </button>
          </div>
          {quotes.length === 0 && <p className="text-white/30 text-sm">No quotes yet. Draft one from supplier pricing in minutes.</p>}
          <div className="space-y-3">
            {quotes.map((q) => (
              <button
                key={q.quote_id}
                onClick={() => navigate(`/admin/quotes/${q.quote_id}`)}
                data-testid={`quote-row-${q.quote_id}`}
                className="w-full flex flex-wrap items-center gap-x-6 gap-y-2 bg-white/[0.03] hover:bg-white/[0.06] border hairline rounded-sm px-5 py-4 text-left transition-colors"
              >
                <span className="inline-flex items-center gap-2 text-sm text-white font-medium"><FileText size={14} className="text-[#A7D7A0]" /> {q.quote_id}</span>
                <span className="text-sm text-[#A7D7A0] font-semibold">{usd(q.selling_price)}</span>
                <span className="text-xs text-white/40">margin {q.gross_margin_pct}%</span>
                {q.escalation_required && <span className="text-[10px] font-mono-tech uppercase tracking-[0.15em] text-[#FFC857]">review required</span>}
                <span className={`ml-auto px-2.5 py-1 rounded-sm border text-[11px] ${QUOTE_STATUS_COLORS[q.status] || QUOTE_STATUS_COLORS.draft}`}>
                  {q.status === "sent" ? "Sent" : "Draft"}{q.approved && q.status !== "sent" ? " · Approved" : ""}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}
